import {
	Product,
	ProductSource,
	ProductMetaInfo,
	ProductKeyValuePair,
	AmazonRefineMents,
	ProductPricingPlan
} from "./index";
import {
	UnifiedSearchItem,
	UnifiedSearchFilters,
	UnifiedPricingResponse,
	UnifiedPricingResult,
	UnifiedPricingItemView,
	UnifiedAggregationsResponse,
	ProductSearchResponseInfo
} from "./UnifiedSearch";

export const buildAggregationFilters = (
	filters: UnifiedSearchFilters
): UnifiedSearchFilters => ({
	source_types: filters.source_types,
	manufacturers: [],
	categories: [],
	icecat_category_ids: [],
	supplier_ids: [],
	exclude_out_of_stock: false,
	filter_by_connected_distributors: filters.filter_by_connected_distributors,
	exclude_drop_ship_wh: false,
	item_types: [],
	sync_sources: []
});

export const buildPricingFilters = (
	searchFilters: UnifiedSearchFilters,
	isInitialLoad: boolean
): UnifiedSearchFilters => {
	if (
		isInitialLoad &&
		searchFilters.source_types.length === 1 &&
		searchFilters.source_types[0] === "self_catalog"
	) {
		return {
			...searchFilters,
			source_types: [
				"self_catalog",
				"custom_distributor",
				"dicker_data",
				"icecat"
			]
		};
	}

	return searchFilters;
};

export const prepareIdentifiersForPricing = (
	items: UnifiedSearchItem[]
): {
	product_search_response_infos: ProductSearchResponseInfo[];
	self_catalog_ids: string[];
} => {
	const product_search_response_infos: ProductSearchResponseInfo[] = [];
	const self_catalog_ids: string[] = [];

	items.forEach(item => {
		product_search_response_infos.push({
			identifier: item.identifier,
			source_type: item.source_type,
			source_id: item.source_id,
			item_id: item.item_id
		});

		if (item.source_type === "self_catalog") {
			if (item.mpn === null || item.mpn === undefined) {
				self_catalog_ids.push(item.identifier);
			}
		}
	});

	return { product_search_response_infos, self_catalog_ids };
};

export const mapSourceTypeToProductSource = (
	sourceType?: string
): ProductSource => {
	if (!sourceType) return ProductSource.Self;

	const mapping: Record<string, ProductSource> = {
		self_catalog: ProductSource.Self,
		self: ProductSource.Self,
		icecat: ProductSource.Icecat,
		custom_distributor: ProductSource.CustomDistributor,
		dicker_data: ProductSource.DickerData
	};

	return mapping[sourceType] || ProductSource.Self;
};

export const prioritizeItemViews = (
	itemViews: UnifiedPricingItemView[]
): UnifiedPricingItemView[] => {
	const priority: Record<string, number> = {
		self_catalog: 1,
		self: 1,
		custom_distributor: 2,
		dicker_data: 3,
		icecat: 4
	};

	return [...itemViews].sort((a, b) => {
		const aPriority = priority[a.product_source || ""] || 999;
		const bPriority = priority[b.product_source || ""] || 999;
		return aPriority - bPriority;
	});
};

const createProductFromSearchItem = (
	searchItem: UnifiedSearchItem
): Product => {
	const product = new Product();

	product.id = searchItem.item_id || searchItem.identifier;
	product.name = searchItem.name;
	product.description = searchItem.description;
	product.item_images = searchItem.images || [];
	product.manufacturer_part_no = searchItem.mpn || undefined;
	product.identifier = searchItem.identifier;
	product.product_source = mapSourceTypeToProductSource(
		searchItem.source_type
	);
	product.pricing = [];
	product.unit_of_measure = undefined;
	product.created = new Date();
	product.updated = new Date();
	product.type = null;
	product.partner_id = "";
	product.logo = "";
	product.vendor_id = "";
	product.is_duplicate_manufacturer_part_no = false;
	product.mandatory_custom_field_check_require = false;

	return product;
};

export const flattenPricingResponseToProducts = (
	pricingResponse: UnifiedPricingResponse,
	searchItems: UnifiedSearchItem[]
): Product[] => {
	const products: Product[] = [];

	// Handle direct distributor search (no search items)
	if (searchItems.length === 0) {
		// For direct distributor search, iterate through pricing results
		pricingResponse.results.forEach(result => {
			if (result.item_views.length === 0) {
				return;
			}

			const prioritizedItemViews = prioritizeItemViews(result.item_views);
			const primaryItemView = prioritizedItemViews[0];

			const product = new Product();

			product.id = primaryItemView.id;
			product.name = primaryItemView.name;
			product.description = primaryItemView.description;
			product.item_images = primaryItemView.item_images;
			product.manufacturer = primaryItemView.manufacturer || undefined;
			product.manufacturer_part_no =
				primaryItemView.manufacturer_part_no || undefined;
			product.identifier =
				primaryItemView.manufacturer_part_no || undefined;
			product.product_source = mapSourceTypeToProductSource(
				primaryItemView.product_source
			);
			product.type = primaryItemView.type;
			product.product_category =
				primaryItemView.product_category || undefined;
			product.vendor_name = primaryItemView.vendor_name || undefined;
			product.vendor_sku_no = primaryItemView.vendor_sku_no || undefined;
			product.custom_fields = primaryItemView.custom_fields;
			product.tax_category_id =
				primaryItemView.tax_category_id || undefined;
			product.created = new Date(primaryItemView.created);
			product.updated = new Date(primaryItemView.updated);
			product.read_only = primaryItemView.read_only;
			product.sync_data = primaryItemView.sync_data;
			product.unit_of_measure =
				primaryItemView.unit_of_measure || undefined;
			product.templates = primaryItemView.templates;
			product.asin = primaryItemView.asin || undefined;
			product.show_individual_product_pricing =
				primaryItemView.show_individual_product_pricing || undefined;
			product.should_add_setup_price_to_margin =
				primaryItemView.should_add_setup_price_to_margin || undefined;
			product.etilize_skus_info =
				primaryItemView.etilize_skus_info || undefined;
			product.icecat_skus_info =
				primaryItemView.icecat_skus_info || undefined;
			product.is_duplicate_manufacturer_part_no =
				primaryItemView.is_duplicate_manufacturer_part_no;
			product.vendor_company_id =
				primaryItemView.vendor_company_id || undefined;
			product.selected_custom_distributor_details =
				primaryItemView.selected_custom_distributor_details ||
				undefined;
			product.show_child_quantity =
				primaryItemView.show_child_quantity || undefined;
			product.show_child_items_in_quote_block =
				primaryItemView.show_child_items_in_quote_block || undefined;

			const allPricing: ProductPricingPlan[] = [];
			result.item_views.forEach(itemView => {
				allPricing.push(...itemView.pricing);
			});
			product.pricing = allPricing;
			product.item_views = result.item_views;

			products.push(product);
		});

		return products;
	}

	// Normal flow: Create pricing result map for quick lookup by item_id
	const pricingResultMap: Record<string, UnifiedPricingResult> = {};
	pricingResponse.results.forEach(result => {
		pricingResultMap[result.item_id] = result;
	});

	// Iterate through search items to preserve search order
	searchItems.forEach(searchItem => {
		const result = pricingResultMap[searchItem.item_id];

		if (!result) {
			// No pricing found - create product from search item only
			console.warn(
				"No pricing result found for item_id:",
				searchItem.item_id
			);
			const productFromSearch = createProductFromSearchItem(searchItem);
			products.push(productFromSearch);
			return;
		}

		if (result.item_views.length === 0) {
			const productFromSearch = createProductFromSearchItem(searchItem);
			products.push(productFromSearch);
			return;
		}

		const prioritizedItemViews = prioritizeItemViews(result.item_views);
		const primaryItemView = prioritizedItemViews[0];

		const product = new Product();

		product.id = primaryItemView.id;

		// Always prefer search data for display fields (name, description, images)
		// Search API is the source of truth for product metadata
		// Pricing API should only provide pricing information
		product.name = searchItem?.name || primaryItemView.name;
		product.description =
			searchItem?.description || primaryItemView.description;
		product.item_images =
			searchItem?.images && searchItem.images.length > 0
				? searchItem.images
				: primaryItemView.item_images;
		product.manufacturer = primaryItemView.manufacturer || undefined;

		const primaryMpn = primaryItemView.manufacturer_part_no;
		const searchMpn = searchItem?.mpn;

		// Always prefer search MPN - it's the user's source of truth
		// Only use pricing MPN if search doesn't have it
		if (searchMpn !== null && searchMpn !== undefined) {
			product.manufacturer_part_no = searchMpn;
		} else if (primaryMpn !== null && primaryMpn !== undefined) {
			product.manufacturer_part_no = primaryMpn;
		} else {
			product.manufacturer_part_no = undefined;
		}

		product.identifier =
			searchItem?.identifier ||
			primaryItemView.manufacturer_part_no ||
			undefined;

		product.product_source = mapSourceTypeToProductSource(
			primaryItemView.product_source || searchItem?.source_type
		);

		product.type = primaryItemView.type;
		product.product_category =
			primaryItemView.product_category || undefined;
		product.vendor_name = primaryItemView.vendor_name || undefined;
		product.vendor_sku_no = primaryItemView.vendor_sku_no || undefined;
		product.custom_fields = primaryItemView.custom_fields;
		product.tax_category_id = primaryItemView.tax_category_id || undefined;
		product.created = new Date(primaryItemView.created);
		product.updated = new Date(primaryItemView.updated);
		product.read_only = primaryItemView.read_only;
		product.sync_data = primaryItemView.sync_data;
		product.unit_of_measure = primaryItemView.unit_of_measure || undefined;
		product.templates = primaryItemView.templates;
		product.asin = primaryItemView.asin || undefined;
		product.show_individual_product_pricing =
			primaryItemView.show_individual_product_pricing || undefined;
		product.should_add_setup_price_to_margin =
			primaryItemView.should_add_setup_price_to_margin || undefined;
		product.etilize_skus_info =
			primaryItemView.etilize_skus_info || undefined;
		product.icecat_skus_info =
			primaryItemView.icecat_skus_info || undefined;
		product.is_duplicate_manufacturer_part_no =
			primaryItemView.is_duplicate_manufacturer_part_no;
		product.vendor_company_id =
			primaryItemView.vendor_company_id || undefined;
		product.selected_custom_distributor_details =
			primaryItemView.selected_custom_distributor_details || undefined;
		product.show_child_quantity =
			primaryItemView.show_child_quantity || undefined;
		product.show_child_items_in_quote_block =
			primaryItemView.show_child_items_in_quote_block || undefined;

		const allPricing: ProductPricingPlan[] = [];
		result.item_views.forEach(itemView => {
			allPricing.push(...itemView.pricing);
		});
		product.pricing = allPricing;
		product.item_views = result.item_views;

		products.push(product);
	});

	return products;
};

export const getItemViewForProduct = (
	product: Product,
	selectedPricingId: string | null | undefined
): UnifiedPricingItemView | null => {
	if (!product.item_views || product.item_views.length === 0) return null;

	if (selectedPricingId) {
		const viewForPricing =
			product.item_views.find(iv =>
				iv.pricing.some(p => p.id === selectedPricingId)
			) ?? null;
		if (viewForPricing) return viewForPricing;
	}

	return prioritizeItemViews(product.item_views)[0] ?? null;
};

export const convertAggregationsToMetaInfo = (
	aggResponse: UnifiedAggregationsResponse
): ProductMetaInfo => {
	const metaInfo = new ProductMetaInfo();

	metaInfo.manufacturers = aggResponse.manufacturers.map(m => {
		const pair = new ProductKeyValuePair();
		pair.label = m.name || m.key;
		pair.value = m.key;
		pair.count = m.count;
		return pair;
	});

	// Keep categories separate - don't merge
	metaInfo.categories = aggResponse.categories.map(c => {
		const pair = new ProductKeyValuePair();
		pair.label = c.name || c.key;
		pair.value = c.key;
		pair.count = c.count;
		return pair;
	});

	metaInfo.icecatCategories = aggResponse.icecatCategories.map(c => {
		const pair = new ProductKeyValuePair();
		pair.label = c.name || c.key;
		pair.value = c.key;
		pair.count = c.count;
		return pair;
	});

	metaInfo.features = [];
	metaInfo.sub_categories = [];
	metaInfo.amazon_refinements = new AmazonRefineMents();

	return metaInfo;
};
